import clsx from "clsx";
import Image from "next/image";
import { useTranslation } from "react-i18next";

import bubbleProfile from "@/assets/newsletter/bubble-profile.png";
import preview from "@/assets/newsletter/preview.png";
import { Header } from "@/components/Header";

interface NewsletterFormProps {
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  isValidEmail: boolean;
}

export function NewsletterForm({
  email,
  onEmailChange,
  onSubmit,
  isLoading,
  isValidEmail,
}: NewsletterFormProps) {
  const { t } = useTranslation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="bg-base-100 flex min-h-screen flex-col">
      {/* 헤더 */}
      <Header title={t("aidol:newsletter.header")} />

      {/* 카드 */}
      <div className="flex flex-1 flex-col items-center p-6">
        <form
          onSubmit={handleSubmit}
          className="bg-base-200 flex w-full max-w-86.25 flex-col gap-6 rounded-lg p-6"
        >
          {/* 타이틀 */}
          <p className="text-title-s text-base-content whitespace-pre-line">
            {t("aidol:newsletter.previewTitle")}
          </p>

          {/* 프리뷰 영역 */}
          <div className="flex flex-col gap-2">
            {/* 채팅 버블 */}
            <div className="bg-base-300 flex items-center gap-2.5 overflow-hidden rounded-lg px-4 py-2">
              <div className="relative size-7 shrink-0 overflow-hidden rounded-full">
                <Image
                  src={bubbleProfile}
                  alt=""
                  fill
                  sizes="28px"
                  className="object-cover"
                />
              </div>
              <p className="text-body-s text-base-content flex-1">
                {t("aidol:newsletter.previewMessage")}
              </p>
            </div>

            {/* 프리뷰 이미지 */}
            <div className="relative h-70 w-full overflow-hidden rounded-lg">
              <Image
                src={preview}
                alt=""
                fill
                sizes="100vw"
                className="object-cover"
              />
            </div>
          </div>

          {/* 이메일 입력 */}
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder={t("aidol:newsletter.emailPlaceholder")}
            className="border-base-300 text-body-m placeholder:text-base-300 w-full rounded-lg border bg-white px-4 py-3 text-black focus:outline-none"
            disabled={isLoading}
          />

          {/* 제출 버튼 */}
          <button
            type="submit"
            disabled={!isValidEmail || isLoading}
            className={clsx(
              "bg-neutral text-neutral-content text-label-l w-full cursor-pointer rounded-lg p-4",
              !isValidEmail && !isLoading && "opacity-20",
            )}
          >
            {isLoading
              ? t("aidol:newsletter.submitting")
              : t("aidol:newsletter.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
